import type { HierarchicalLogger } from '@atlassian/logger-interface';
import type { Metrics } from '@atlassian/metrics-interface';
import type { SerializableRecord } from '@post-office/serializable-record';
import { type Statsig } from '@post-office/statsig-node-client';

import { type GraphqlClients } from './graphql-clients';
import type { PreDefinedFilterMetadata } from './pre-defined-filters';
import { type RestApiClients } from './rest-api-clients';
import { type TapProvider } from './tap-provider';
import { type ExecutionRuntime } from './execution-runtime';
import { type AccountIdRecipient, type GroupedRecipient, type Recipient } from '../recipients';
import type { RequestContext } from '../shared-request-middleware-context/requestContext';
import type { SharedRequestMiddlewareContext } from '../shared-request-middleware-context/context';
import type { MessageToFilter, MessageToOrchestrate, OrchestrationContext } from '../types';
import type { ActiveScopes } from '../util';
import { IdentityUserAri } from '@atlassian/ari';

export const HISTOGRAM_LATENCY_INTERVAL_BUCKETS: number[] = [
	5, 10, 25, 50, 100, 150, 200, 250, 500, 1000,
];

// https://switcheroo.atlassian.com/ui/gates/5ff82fe4-0e85-4521-8282-65d373d9607f/key/post-office_enable-new-filter-error
const ENABLE_NEW_FILTER_ERROR_FEATURE_GATE = 'post-office_enable-new-filter-error';

export type FilterClients = Partial<
	Pick<
		RequestContext,
		'statsig' | 'graphqlClients' | 'restApiClients' | 'messageInstanceEventService' | 'tapProvider'
	>
> & {
	executionRuntime?: ExecutionRuntime;
};
export type FilterResponses = FilterResponse | FilterResponse[];
export type FilterExecutionResponse = Omit<FilterResponse, 'filterName'>;
export type FilterExecutionResponses = FilterExecutionResponse | FilterExecutionResponse[];

export enum FilterResult {
	Allow = 'allow',
	Deny = 'deny',
	Skip = 'skip',
	Fail = 'fail',
	Defer = 'defer',
}

export type RedactionLevel =
	| 'redact-email-spam'
	| 'redact-filter-content-policy'
	| 'redact-eus-policy';

export type DeliveryChannel =
	| 'chat-app-notification'
	| 'email'
	| 'in-app'
	| 'in-app-v2'
	| 'microsoft-teams'
	| 'browser-push'
	| 'mobile-push'
	| 'notifications-platform'
	| 'notification-drawer'
	| 'no-delivery-channel';

export interface FilterResponse {
	result: FilterResult;
	filterName: string;
	failureReason?: string;
	latencyInMillis?: number;
	criteria?:
		| {
				type: 'deliveryChannel';
				deliveryChannel: DeliveryChannel;
		  }
		| {
				type: 'placementId';
				placementId: string;
		  }
		| {
				type: 'deliveryMethod';
				deliveryMethod: 'regular' | 'digest';
		  };
	redactionLevel?: RedactionLevel;
}

export type AdditionalFilterContext = {
	placementId?: string;
	product?: string;
	subproduct?: string;
	tenantId?: string;
	orgId?: string;
	workspaceId?: string;
	workspaceAri?: string;
	activeScopes?: ActiveScopes;
	cookies?: OrchestrationContext['cookies'];
	headers?: SharedRequestMiddlewareContext['headers'];
};

export type FilterCriteriaContext = {
	deliveryChannel?: string;
	placementId?: string;
	deliveryMethod?: 'regular' | 'digest';
};

/**
 * Enhances a FilterResponse with helper methods for criteria matching and result checking.
 * This allows the matching logic to be encapsulated within the response object itself.
 */
export const withFilterMethods = (
	response: FilterResponse,
): FilterResponse & {
	shouldSkip: (context: FilterCriteriaContext) => boolean;
	shouldBreakIfDeny: () => boolean;
	shouldBreakIfFail: () => boolean;
	hasRedactionLevel: () => boolean;
} => {
	return {
		...response,
		shouldSkip: (context: FilterCriteriaContext): boolean => {
			const criteria = response.criteria;
			if (!criteria) {
				return false;
			}

			if (criteria.type === 'deliveryChannel') {
				return (
					context.deliveryChannel !== undefined &&
					criteria.deliveryChannel !== context.deliveryChannel
				);
			}

			if (criteria.type === 'placementId') {
				return context.placementId !== undefined && criteria.placementId !== context.placementId;
			}

			if (criteria.type === 'deliveryMethod') {
				// if we ARE NOT currently evaluating a message that will be rolled up into a digest
				// ignore a digest enrolled preference response that relates to digests
				return (
					context.deliveryMethod === undefined || criteria.deliveryMethod !== context.deliveryMethod
				);
			}

			return false;
		},
		shouldBreakIfDeny: (): boolean => {
			return response.result === FilterResult.Deny;
		},
		shouldBreakIfFail: (): boolean => {
			return response.result === FilterResult.Fail;
		},
		hasRedactionLevel: (): boolean => {
			return response.redactionLevel !== undefined;
		},
	};
};

const REJECTED_FILTER_RESPONSE: FilterResponse = {
	result: FilterResult.Fail,
	filterName: 'unknown',
	failureReason: 'Unknown error',
};

/**
 * Base message filter types.
 */
type MaybeAsync<T> = Promise<T> | T;

export type AnyMessageFilterExecute = (
	...args: [{ context: SerializableRecord }, ...never]
) => MaybeAsync<FilterExecutionResponse | FilterExecutionResponse[]>;

type MessageFilterExecuteConstraint<T extends AnyMessageFilterExecute> = T;

/**
 * Universal Filter types. Filters that match these types should be able to be used
 * in all filter locations.
 */
type UniversalMessageFilterFunctionExecute<
	TContext extends SerializableRecord = SerializableRecord,
> = MessageFilterExecuteConstraint<
	(message: { context: TContext }) => Promise<FilterExecutionResponse | FilterExecutionResponse[]>
>;

export type UniversalMessageFilterFunction<
	TContext extends SerializableRecord = SerializableRecord,
> = {
	execute: UniversalMessageFilterFunctionExecute<TContext>;
};

/**
 * Message Template Filter types
 */
type MessageTemplateFilterExecute<
	TContext extends SerializableRecord = SerializableRecord,
	TRecipient extends Recipient = AccountIdRecipient,
> = MessageFilterExecuteConstraint<
	(
		message: MessageToFilter<TContext, TRecipient>,
		logger?: HierarchicalLogger,
		filterClients?: FilterClients,
		additionalContext?: AdditionalFilterContext,
		metrics?: Metrics,
	) => Promise<FilterExecutionResponse | FilterExecutionResponse[]>
>;

type InternalFilterExecute<TContext extends SerializableRecord = SerializableRecord> =
	MessageFilterExecuteConstraint<
		(
			message: MessageToOrchestrate<TContext>,
			logger?: HierarchicalLogger,
			filterClients?: FilterClients,
			additionalContext?: AdditionalFilterContext,
		) => Promise<FilterExecutionResponse | FilterExecutionResponse[]>
	>;

type PreDefinedMessageTemplateFilter = {
	execute: PreDefinedFilterMetadata;
};

export type MessageTemplateMessageFilter<
	TContext extends SerializableRecord = SerializableRecord,
	TRecipient extends Recipient = AccountIdRecipient,
> = {
	execute: MessageTemplateFilterExecute<TContext, TRecipient>;
	filterName: string;
};

export type FilterFunction<TContext extends SerializableRecord = SerializableRecord> =
	MessageTemplateFilterExecute<TContext>;

export type InternalFilterFunction<TContext extends SerializableRecord = SerializableRecord> =
	InternalFilterExecute<TContext>;

export type UniversalMessageFilter<TContext extends SerializableRecord = SerializableRecord> =
	UniversalMessageFilterFunction<TContext>;

export type MessageFilter<
	TContext extends SerializableRecord = SerializableRecord,
	TRecipient extends Recipient = AccountIdRecipient,
> = PreDefinedMessageTemplateFilter | MessageTemplateMessageFilter<TContext, TRecipient>;

export class FilterError extends Error {
	constructor(
		error: Error,
		public readonly additionalInfo: { filterName: string; [propName: string]: unknown },
	) {
		super(error.message, { cause: error });
		this.name = this.constructor.name;
	}
}

export type MessageFiltersRunner = (
	message: MessageToFilter<SerializableRecord, Exclude<Recipient, GroupedRecipient>>,
	metrics: Metrics,
	logger: HierarchicalLogger,
	statsig: Statsig,
	additionalFilterContext?: AdditionalFilterContext,
	restApiClients?: RestApiClients,
	graphqlClients?: GraphqlClients,
	tapProvider?: TapProvider,
	executionRuntime?: ExecutionRuntime,
) => Promise<FilterResponse[]>;

type FilterWithFilterName = Pick<MessageTemplateMessageFilter, 'filterName'>;
type FilterWithResolvedName = MessageFilter | FilterWithFilterName;

const resolveFilterNameForResponse = (
	response: FilterExecutionResponse,
	filterName: string,
): FilterResponse => {
	return {
		...response,
		filterName,
	};
};

const withResolvedFilterName = (
	filterResponses: FilterExecutionResponses,
	filter: FilterWithResolvedName,
): FilterResponses => {
	const filterName = resolveFilterName(filter);

	if (Array.isArray(filterResponses)) {
		return filterResponses.map((response) => resolveFilterNameForResponse(response, filterName));
	}

	return resolveFilterNameForResponse(filterResponses, filterName);
};

export const runMessageTemplateFilters: MessageFiltersRunner = async (
	message,
	metrics,
	logger,
	statsig,
	additionalFilterContext,
	restApiClients,
	graphqlClients,
	tapProvider,
	executionRuntime,
) => {
	const filters = message.messageTemplate.messageFilters;
	if (!Array.isArray(filters) || filters.length === 0) {
		return [];
	}

	const filterPromises: Promise<FilterResponses>[] = [];
	for (const filter of filters) {
		// TODO: Look at improving this type validation and in filters-processor.ts
		if (typeof filter.execute === 'function') {
			const startTime = new Date().getTime();

			const promise = filter
				.execute(
					message as MessageToFilter,
					logger,
					{
						restApiClients,
						statsig,
						graphqlClients,
						tapProvider,
						executionRuntime,
					},
					additionalFilterContext,
					metrics,
				)
				.then((res) => {
					return recordLatencyForFilterResult(
						withResolvedFilterName(res, filter),
						startTime,
						metrics,
					);
				})
				.catch((err: Error) => {
					const useNewFilterErrorHandler = statsig.checkGate(
						{
							identifiers: {
								atlassianAccountId:
									message.recipient.type === 'AccountId'
										? IdentityUserAri.parse(message.recipient.userAri).userId
										: 'unknown',
							},
						},
						ENABLE_NEW_FILTER_ERROR_FEATURE_GATE,
					);

					let result: FilterResponses;
					if (useNewFilterErrorHandler) {
						result = handleFilterErrorNew({
							logger,
							metrics,
							error: err,
							message,
							startTime,
							filter,
							isMessageTemplateFilter: true,
						});
					} else {
						result = handleFilterError(logger, metrics, err, message, startTime);
					}

					return Promise.reject(result);
				});
			filterPromises.push(promise);
		}
	}
	const filterResponses = await Promise.allSettled(filterPromises);

	return unpackFilterResults(filterResponses);
};

const recordFilterMetrics = ({
	filterResponses,
	startTime,
	metrics,
	isMessageTemplateFilter,
}: {
	filterResponses: FilterResponses;
	startTime: number;
	metrics: Metrics;
	isMessageTemplateFilter: boolean;
}): void => {
	if (!isMessageTemplateFilter) {
		recordFilterReliabilityMetric(filterResponses, metrics);
	}
	recordLatencyForFilterResult(filterResponses, startTime, metrics);
};

export const recordLatencyForFilterResult = (
	filterResponse: FilterResponses,
	start: number,
	metrics: Metrics,
): FilterResponses => {
	const latency = new Date().getTime() - start;

	if (Array.isArray(filterResponse)) {
		filterResponse.map((res) => {
			res.latencyInMillis = latency;
			const metricName = `post-office.orchestration.filter-check.${res.filterName}`;
			metrics
				.histogram(metricName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS, {
					filter: res.filterName,
				})
				.set(latency);
		});
	} else {
		filterResponse.latencyInMillis = latency;
		const metricName = `post-office.orchestration.filter-check.${filterResponse.filterName}`;
		metrics
			.histogram(metricName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS, {
				filter: filterResponse.filterName,
			})
			.set(latency);
	}
	return filterResponse;
};

const FILTERING_RELIABILITY_METRIC_NAME = 'post-office.orchestration.filter-operation';
const FILTERING_RELIABILITY_FAILURE_DETAILED_METRIC_NAME =
	'post-office.orchestration.filter-operation-failures';

export const recordFilterReliabilityMetric = (
	filterResponses: FilterResponses,
	metrics: Metrics,
): void => {
	const filterArray = Array.isArray(filterResponses) ? filterResponses : [filterResponses];

	const filterFailures = filterArray.filter((response) => response.result === FilterResult.Fail);

	const wasFilterFailure = filterFailures.length > 0;

	metrics
		.counter(FILTERING_RELIABILITY_METRIC_NAME, {
			result: wasFilterFailure ? 'error' : 'success',
		})
		.incr();

	for (const filterFailure of filterFailures) {
		metrics
			.counter(FILTERING_RELIABILITY_FAILURE_DETAILED_METRIC_NAME, {
				filterName: filterFailure.filterName,
			})
			.incr();
	}
};

export const unpackFilterResults = (
	results: PromiseSettledResult<FilterResponses>[],
): FilterResponse[] => {
	const filterResponses: FilterResponse[] = results.flatMap((promiseResult) => {
		if (promiseResult.status === 'fulfilled') {
			return promiseResult.value;
		} else {
			return REJECTED_FILTER_RESPONSE;
		}
	});
	return filterResponses;
};

const handleFilterError = (
	logger: HierarchicalLogger,
	metrics: Metrics,
	error: Error,
	message: MessageToOrchestrate,
	startTime: number,
): FilterResponses => {
	logger.error(
		{
			messageInstanceId: message.messageInstanceId,
			// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
			error: error,
		},
		'Error resolving filter',
	);

	recordFilterMetrics({
		filterResponses: REJECTED_FILTER_RESPONSE,
		startTime,
		metrics,
		isMessageTemplateFilter: false,
	});
	return REJECTED_FILTER_RESPONSE;
};

/**
 * Handles filter execution errors with logging, metrics, and structured error responses.
 *
 * Meant to replace {@link handleFilterError} with richer error context and structured
 * filter responses.
 *
 * @param params - Object containing logger, metrics, error, message, startTime, and filter
 * @returns Rejected filter response with filter name and failure reason
 */
export const handleFilterErrorNew = ({
	logger,
	metrics,
	error,
	message,
	startTime,
	filter,
	isMessageTemplateFilter,
}: {
	logger: HierarchicalLogger;
	metrics: Metrics;
	error: Error;
	message: MessageToOrchestrate;
	startTime: number;
	filter: MessageFilter;
	isMessageTemplateFilter: boolean;
}): FilterResponses => {
	const filterName = resolveFilterName(filter);

	const filterError = new FilterError(error, { filterName });

	logger.error(
		{
			messageInstanceId: message.messageInstanceId,
			error: filterError,
			filterName,
		},
		'Error resolving filter',
	);

	const rejectedFilterResponse = {
		result: FilterResult.Fail,
		filterName,
		failureReason: error.message,
	};

	recordFilterMetrics({
		filterResponses: rejectedFilterResponse,
		startTime,
		metrics,
		isMessageTemplateFilter,
	});

	return rejectedFilterResponse;
};

/**
 * Resolves the filter name for logging and error reporting.
 *
 * For PreDefinedMessageTemplateFilter, uses filter.execute.name.
 * For other filter types (e.g. SystemFilter), uses filter.filterName if available.
 * If there is no filter name, it is likely a custom message template filter.
 *
 * @param filter - The filter to resolve the name for
 * @returns The resolved filter name
 */
export const resolveFilterName = (filter: FilterWithResolvedName): string => {
	if (isPreDefinedMessageFilterMetadata(filter)) {
		return filter.execute.name;
	}
	return filter.filterName;
};

const isPreDefinedMessageFilterMetadata = (
	filter: FilterWithResolvedName,
): filter is PreDefinedMessageTemplateFilter => {
	return 'execute' in filter && typeof filter.execute !== 'function' && 'name' in filter.execute;
};

/**
 * Executes a filter with metrics tracking and error handling.
 * Wraps the filter execution with timing metrics and standardized error handling.
 *
 * @param filter - The filter to execute, must have an execute method
 * @param message - The message to filter
 * @param logger - Logger instance for error logging
 * @param metrics - Metrics instance for tracking performance
 * @returns Promise that resolves with filter responses or rejects with error responses
 */
export const executeStandardFilterWithMetrics = async <
	TContext extends SerializableRecord = SerializableRecord,
>(
	filter: {
		execute: (message: MessageToOrchestrate<TContext>) => Promise<FilterExecutionResponses>;
	} & FilterWithResolvedName,
	message: MessageToOrchestrate<TContext>,
	logger: HierarchicalLogger,
	metrics: Metrics,
	statsig: Statsig,
): Promise<FilterResponses> => {
	const startTime = new Date().getTime();
	try {
		const res = withResolvedFilterName(await filter.execute(message), filter);
		recordFilterMetrics({
			filterResponses: res,
			startTime,
			metrics,
			isMessageTemplateFilter: false,
		});
		return res;
	} catch (err) {
		const useNewFilterErrorHandler = statsig.checkGate(
			{
				identifiers: {
					atlassianAccountId:
						message.recipient.type === 'AccountId'
							? IdentityUserAri.parse(message.recipient.userAri).userId
							: 'unknown',
				},
			},
			ENABLE_NEW_FILTER_ERROR_FEATURE_GATE,
		);

		let result: FilterResponses;
		if (useNewFilterErrorHandler) {
			result = handleFilterErrorNew({
				logger,
				metrics,
				error: err as Error,
				message,
				startTime,
				filter: filter as MessageTemplateMessageFilter<TContext>,
				isMessageTemplateFilter: false,
			});
		} else {
			result = handleFilterError(logger, metrics, err as Error, message, startTime);
		}

		throw result;
	}
};
